<template>
  <div class="sf-grid" data-testid="export-index">
    <div class="sf-row sf-row-center sf-below-3">
      <div class="sf-col">
        <ExportLedgerSection
          v-for="(exportsByLedger, index) in exports"
          :key="`${exportsByLedger.ledger}-${index}`"
          :ledger="exportsByLedger.ledger"
          :exports="exportsByLedger.exports"
          :app-links="appLinks"
          :opened-dropdown-export-id="openedDropdownExportId"
          @toggle-dropdown="handleToggleDropdown"
          @close-dropdown="handleCloseDropdown"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  AppLink,
  ExportBundle,
  ExportType,
} from '@app/application/export/types'

import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'

import { SfLog } from '@app/adapters/logger'
import useSfPolling from '@app/shared/vue/composables/use-sf-polling'
import SFetch from '@app/utils/sfetch'
import ExportLedgerSection from '@app/application/export/screens/exports/components/export-ledger-section.vue'

defineOptions({
  name: 'ExportIndex',
})

interface Props {
  exports: ExportBundle[]
  appLinks: AppLink[]
  lastMessagebusId: number
  company: {
    companyId: string
    firmId: string
  }
}

const props = defineProps<Props>()

const { startPolling } = useSfPolling()

onBeforeMount(() => {
  setupSubscription()
})

onBeforeUnmount(() => {
  removeSubscription()
})

const exports = ref(props.exports)

const allExportObjects = computed(() =>
  exports.value.flatMap((exportsByLedger) => exportsByLedger.exports)
)

const openedDropdownExportId = ref<null | number>(null)
let unsubscribe: null | (() => void)

const hasUnfinishedExports = computed(() => {
  const unfinishedExports = allExportObjects.value.filter(
    (el) => el.status !== 'succeeded'
  )

  return unfinishedExports.length > 0
})

function exportsCompleted(response: { export_id: number }) {
  const exportId = response.export_id
  updateExport(exportId)
}

function handleCloseDropdown() {
  openedDropdownExportId.value = null
}

function handleToggleDropdown(exportId: number) {
  openedDropdownExportId.value =
    openedDropdownExportId.value === exportId ? null : exportId
}

function setupSubscription() {
  removeSubscription()

  unsubscribe = startPolling(
    '/exports/completed',
    props.lastMessagebusId,
    () => hasUnfinishedExports.value,
    exportsCompleted
  )
}

function removeSubscription() {
  if (unsubscribe) {
    unsubscribe()
    unsubscribe = null
  }
}

async function updateExport(exportId: number) {
  const exportItem = allExportObjects.value.find(
    (element) => element.id === exportId
  )

  if (exportItem) {
    try {
      const newExport = await SFetch.json<ExportType>(exportItem.url)

      const exportBundle = exports.value.find(
        (exportsByLedger) => exportsByLedger.ledger === exportItem.ledger
      )

      if (exportBundle) {
        const exportIndex = exportBundle.exports.findIndex(
          (el) => el.id === exportId
        )

        if (exportIndex !== -1 && newExport) {
          exportBundle.exports[exportIndex] = newExport
        }
      }
    } catch (e) {
      SfLog.info('Failed to load updated export', {
        extra: {
          companyId: props.company.companyId,
          firmId: props.company.firmId,
          exportId,
          error: e,
        },
      })
    }
  }
}
</script>
